import React, { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { ChevronRight } from 'lucide-react';

import { SearchIcon } from '../../components/icons/search';
import { EditIcon } from '../../components/icons/edit';
import { JournalConsultationFormatIcon } from '../../components/journal/journal-consultation-format-icon';
import { IClient, IConsultation } from '../../store/types';
import { declOfNum } from '../../utils/decOfNum';
import { getTime } from '../../utils/getTime';
import { SessionDetail, buildSessionDetailSession } from '../../components/journal/session-detail';

type Tab = {
  id: number;
  title: string;
};

interface JournalMobileProps {
  tabs: Tab[];
  activeTab: number;
  setActiveTab: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  clients: IClient[];
  consultations: IConsultation[];
  activeClient: number | null;
  setActiveClient: Dispatch<SetStateAction<number | null>>;
  onEditClient: (client: IClient) => void;
  pendingSessionDetail?: { consultationId: number; initialTab: string } | null;
  onOpenSessionDetail: (consultationId: number, initialTab: string) => void;
  onPendingSessionDetailHandled: () => void;
}

export const JournalMobile: FC<JournalMobileProps> = ({
  tabs,
  activeTab,
  setActiveTab,
  search,
  setSearch,
  clients,
  consultations,
  activeClient,
  setActiveClient,
  onEditClient,
  pendingSessionDetail = null,
  onOpenSessionDetail,
  onPendingSessionDetailHandled,
}) => {
  const [sessionDetail, setSessionDetail] = useState<{ consultationId: number; initialTab: string } | null>(null);
  const sortedClients = useMemo(() => {
    if (!clients?.length) {
      return [];
    }

    return [...clients].sort((a, b) => a.full_name.localeCompare(b.full_name, 'ru', { sensitivity: 'base' }));
  }, [clients]);

  const sortedConsultations = useMemo(() => {
    if (!consultations?.length) {
      return [];
    }

    return [...consultations].sort(
      (a, b) => new Date(b.datetime_start).getTime() - new Date(a.datetime_start).getTime(),
    );
  }, [consultations]);

  const consultationsLabel = `${sortedConsultations.length} ${declOfNum(sortedConsultations.length || 0, [
    'сессия',
    'сессии',
    'сессий',
  ])}`;

  const selectedTabTitle = tabs.find((tab) => tab.id === activeTab)?.title ?? 'Клиенты';
  const selectedClientName = sortedClients.find((client) => client.id === activeClient)?.full_name;
  const sessionDetailConsultation = useMemo(
    () => (sessionDetail ? sortedConsultations.find((item) => item.id === sessionDetail.consultationId) : undefined),
    [sessionDetail, sortedConsultations],
  );

  useEffect(() => {
    if (!pendingSessionDetail) return;

    const consultation = sortedConsultations.find((item) => item.id === pendingSessionDetail.consultationId);
    if (!consultation) return;

    setSessionDetail(pendingSessionDetail);
    onPendingSessionDetailHandled();
  }, [onPendingSessionDetailHandled, pendingSessionDetail, sortedConsultations]);

  const handleClientSelect = (clientId: number) => {
    setActiveClient(clientId);
  };

  const handleOpenSession = (consultationId: number) => {
    onOpenSessionDetail(consultationId, 'Инструменты');
  };

  const renderClientChip = (client: IClient) => {
    const isActive = client.id === activeClient;
    return (
      <div
        key={client.id}
        className={classNames(
          'flex min-h-11 flex-shrink-0 items-stretch overflow-hidden rounded-full border text-sm font-medium transition-colors duration-200',
          {
            'bg-[#C70000] border-[#C70000] text-white shadow-[0_8px_20px_rgba(199,0,0,0.35)]': isActive,
            'bg-white border-gray-200 text-gray-800': !isActive,
          },
        )}
      >
        <button
          type="button"
          aria-pressed={isActive}
          onClick={() => handleClientSelect(client.id)}
          className="min-w-0 max-w-[190px] touch-manipulation truncate px-4 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#C70000]"
        >
          {client.full_name}
        </button>
        {isActive && (
          <button
            type="button"
            aria-label={`${activeTab === 3 ? 'Редактировать встречу' : 'Редактировать клиента'} ${client.full_name}`}
            className="flex min-h-11 min-w-11 touch-manipulation items-center justify-center border-l border-white/20 bg-white/10 text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white"
            onClick={() => onEditClient(client)}
          >
            <EditIcon width={18} height={18} stroke="currentColor" />
          </button>
        )}
      </div>
    );
  };

  const renderConsultationRow = (consultation: IConsultation, index: number) => {
    const sessionDate = new Date(consultation.date);
    const sessionStart = new Date(consultation.datetime_start);
    const dateLabel = new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(sessionDate);
    const timeLabel = getTime(sessionStart);

    return (
      <button
        type="button"
        key={consultation.id ?? index}
        onClick={() => handleOpenSession(consultation.id)}
        className="group flex min-h-[88px] w-full touch-manipulation items-center gap-3 rounded-2xl border border-[#EAEBEA] bg-white px-4 py-4 text-left [content-visibility:auto] transition-colors hover:border-[#C00000] active:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C00000] focus-visible:ring-offset-2"
        aria-label={`Открыть сессию ${consultation.consultation_number ?? index + 1} от ${dateLabel}, ${timeLabel}`}
      >
        <span className="w-5 shrink-0 font-comfortaa text-base font-bold leading-tight text-[#111026]">
          {consultation.consultation_number ?? index + 1}
        </span>
        <JournalConsultationFormatIcon consultation={consultation} />
        <time dateTime={consultation.datetime_start} className="min-w-0 flex-1 font-manrope text-base text-[#111026]">
          <span className="block tabular-nums">{dateLabel}</span>
          <span className="block tabular-nums text-sm text-gray-500">{timeLabel}</span>
        </time>
        <span className="ml-auto inline-flex min-h-11 shrink-0 items-center gap-1 font-comfortaa text-sm font-semibold text-[#737478] transition-colors group-hover:text-[#C00000]">
          <span className="hidden xs:inline">Открыть</span>
          <ChevronRight size={20} aria-hidden />
        </span>
      </button>
    );
  };

  if (sessionDetail) {
    return (
      <div className="w-full min-w-0 overflow-x-hidden px-4 pb-[85px] pt-4">
        {sessionDetailConsultation ? (
          <SessionDetail
            consultationId={sessionDetail.consultationId}
            session={buildSessionDetailSession(sessionDetailConsultation)}
            initialTab={sessionDetail.initialTab}
            clientName={selectedClientName}
            onBack={() => setSessionDetail(null)}
          />
        ) : (
          <div className="rounded-2xl bg-white p-5 text-center text-sm text-gray-500">
            Сессия не найдена в списке
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 overflow-x-hidden pb-[85px]">
      <div className="min-h-[420px] rounded-[24px] bg-[#F8F9FA] p-4 shadow-[0px_20px_70px_rgba(0,0,0,0.08)]">
        <h1 className="mb-5 text-pretty font-comfortaa text-2xl font-bold text-blackBase">
          Терапевтический журнал
        </h1>

        <div className="mb-5">
          <div
            className="flex gap-2 overflow-x-auto rounded-2xl bg-white p-1 shadow-[0px_6px_20px_rgba(0,0,0,0.08)]"
            role="group"
            aria-label="Раздел журнала"
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                aria-pressed={tab.id === activeTab}
                className={classNames(
                  'min-h-11 flex-none touch-manipulation whitespace-nowrap rounded-xl px-4 py-2 text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C70000] focus-visible:ring-offset-1',
                  {
                    'bg-[#C70000] text-white shadow-[0px_8px_20px_rgba(199,0,0,0.3)]': tab.id === activeTab,
                    'text-gray-600': tab.id !== activeTab,
                  },
                )}
              >
                {tab.id === 1 ? 'Активные' : tab.id === 2 ? 'Архив' : 'Группы'}
              </button>
            ))}
          </div>
        </div>

        <div className="relative mb-6">
          <label htmlFor="journal-mobile-search" className="sr-only">
            {activeTab === 3 ? 'Поиск встречи' : 'Поиск клиента'}
          </label>
          <input
            id="journal-mobile-search"
            name="journal-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={activeTab === 3 ? 'Поиск встречи…' : 'Поиск клиента…'}
            autoComplete="off"
            className="min-h-12 w-full rounded-2xl border border-gray-200 bg-white py-3 pl-11 pr-4 text-gray-900 placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C70000]/50"
          />
          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden>
            <SearchIcon />
          </span>
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-gray-900">{selectedTabTitle}</h2>
            <span className="text-sm text-gray-500">{`${sortedClients.length} ${declOfNum(
              sortedClients.length || 0,
              activeTab === 3 ? ['встреча', 'встречи', 'встреч'] : ['клиент', 'клиента', 'клиентов'],
            )}`}</span>
          </div>
          {sortedClients.length ? (
            <div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-2">
              {sortedClients.map((client) => renderClientChip(client))}
            </div>
          ) : (
            <div className="text-sm text-gray-500">По вашему запросу ничего не найдено</div>
          )}
        </div>

        <main className="flex flex-col flex-grow">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                {activeTab === 3 ? 'Журнал встречи' : 'Журнал клиента'}
              </h2>
              {selectedClientName && <p className="text-sm text-gray-500 mt-1">{selectedClientName}</p>}
            </div>
            <span className="text-sm text-gray-500">{consultationsLabel}</span>
          </div>

          {sortedConsultations.length > 0 ? (
            <div className="space-y-3">
              {sortedConsultations.map((consultation, index) => renderConsultationRow(consultation, index))}
            </div>
          ) : (
            <div className="rounded-2xl bg-white p-5 text-center text-sm text-gray-500 shadow-[0px_10px_30px_rgba(0,0,0,0.04)]">
              {activeClient
                ? activeTab === 3
                  ? 'Для выбранной встречи ещё нет записей в журнале'
                  : 'Для выбранного клиента ещё нет записей в журнале'
                : activeTab === 3
                  ? 'Выберите встречу, чтобы увидеть журнал'
                  : 'Выберите клиента, чтобы увидеть его журнал'}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default JournalMobile;
